import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@cargomatic/ui';
import USER_TYPES from 'constants/UserTypes';
import DeclineAllComponent from './DeclineAllComponent';

const defaultApproveMessage = 'CONFIRM_APPROVAL_MESSAGE';
const missingDocsApproveMessage = 'CONFIRM_APPROVE_MESSAGE_MISSING_DOCS';
const approveTitle = 'CONFIRM_APPROVAL_TITLE';
const declineTitle = 'CONFIRM_DECLINE_TITLE';
const approveAllMessage = 'CONFIRM_APPROVAL_ALL_MESSAGE';

const AccessorialConfirmModal = ({
  show,
  isBulk,
  isDecline,
  confirmType,
  accessorial,
  isLoading = false,
  onConfirm,
  onHide,
}) => {
  const { t } = useTranslation();
  const [declineNote, setDeclineNote] = useState('');
  const [declineReasonCode, setDeclineReasonCode] = useState(null);
  const [errors, setErrors] = useState({ declineNote: {} });

  // Reset internal state when modal closes
  useEffect(() => {
    if (!show) {
      setDeclineNote('');
      setDeclineReasonCode(null);
      setErrors({ declineNote: {} });
    }
  }, [show]);

  const handleDeclineNote = useCallback((value) => {
    if (value && value !== '') {
      setErrors((prev) => ({ ...prev, declineNote: {} }));
    }
    setDeclineNote(value);
  }, []);

  const handleDeclineReasonCode = useCallback((value) => {
    if (value) {
      setDeclineReasonCode(value);
    }
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm({ declineNote, declineReasonCode });
  }, [onConfirm, declineNote, declineReasonCode]);

  // Compute primaryDisabled
  let primaryDisabled;
  if (isBulk) {
    primaryDisabled =
      isDecline &&
      accessorial.payee_type === USER_TYPES.CARRIER &&
      !declineReasonCode;
  } else if (isDecline) {
    const isNotFilledCorrectly =
      confirmType === USER_TYPES.CARRIER
        ? (!declineReasonCode &&
            accessorial.payee_type !== USER_TYPES.VENDOR) ||
          !declineNote.trim()
        : !declineNote.trim();
    primaryDisabled = isNotFilledCorrectly;
  } else {
    primaryDisabled = false;
  }

  // Determine title
  const title = isDecline ? t(declineTitle) : t(approveTitle);

  // Determine message
  let message;
  if (isDecline) {
    const showReasonCodeDropdown = isBulk
      ? accessorial.payee_type === USER_TYPES.CARRIER
      : confirmType === USER_TYPES.CARRIER &&
        accessorial.payee_type === USER_TYPES.CARRIER;

    message = (
      <DeclineAllComponent
        error={errors.declineNote}
        value={declineNote}
        declineReasonCode={declineReasonCode}
        onNoteChange={handleDeclineNote}
        onDeclineReasonCodeChange={
          showReasonCodeDropdown && handleDeclineReasonCode
        }
        confirmType={confirmType}
      />
    );
  } else {
    const approveMsg = isBulk
      ? approveAllMessage
      : accessorial?.missing_docs === true
        ? missingDocsApproveMessage
        : defaultApproveMessage;
    message = t(approveMsg);
  }

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => !open && !isLoading && onHide()}
    >
      <DialogContent
        data-testid={isBulk ? 'ConfirmModal-67dbe1b4' : 'ConfirmModal-03f9282c'}
        className="sm:max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => isLoading && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only" />
        </DialogHeader>
        <div className="py-2">{message}</div>
        <DialogFooter>
          <div
            data-testid="div-f874ed5f"
            className="flex justify-end items-center space-x-2"
          >
            <Button
              data-testid="button-ac75ae48"
              variant="outline"
              disabled={isLoading}
              onClick={onHide}
            >
              {t('CANCEL')}
            </Button>
            <Button
              data-testid="button-6afbdc85"
              disabled={primaryDisabled || isLoading}
              onClick={handleConfirm}
              className="bg-green-cm hover:bg-green-700 text-white"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('OK')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AccessorialConfirmModal;
